import React from 'react';
import {
    Responsive,
    Filter,
    Datagrid,
    ListButton,
    ShowButton,
    EditButton,
    SimpleShowLayout,
    SimpleForm,
    SimpleList,
    TextField,
    BooleanField,
    TextInput,
    ReferenceInput,
    SelectInput,
    required
} from 'admin-on-rest';
import {DependentInput} from 'aor-dependent-input';
import List from '../admin/List';
import Create from '../admin/Create';
import ShowWithType from '../admin/ShowWithType';
import EditWithType from '../admin/EditWithType';
import VirtualizedSelectInput from '../components/VirtualizedSelectInput';
import ProductCategoryByTypeDependentSelectInput from '../components/ProductCategoryByTypeDependentSelectInput';
import ViewButtonWithType from '../buttons/ViewButtonWithType';
import EditButtonWithType from '../buttons/EditButtonWithType';
import TooltipIcon from '../components/TooltipIcon';
import ReactTooltip from 'react-tooltip';
import ColorPickerTextInput from '../components/ColorPickerTextInput';
import {viewStyles} from '../shared/styles-view';
import {CardActions} from 'material-ui/Card';
import Avatar from 'material-ui/Avatar';
import Icon from 'material-ui/svg-icons/action/class';
import {RefreshButton} from 'admin-on-rest/lib/mui/button';

export const fuseProductCategoryIcon = Icon;

const styles = {
    rowShowField: {minWidth: 150},
    fieldSmall: {flex: '0 1 10%', display: 'inline-block', maxWidth: '8.5em', overflow: 'hidden'}
};

const getBasePathUpOne = (basePath) => {
    return basePath
        .split('/')
        .slice(0, -1)
        .join('/');
};

/*
 * View: List
 */

// Create Search and Filter: Product Type
const ProductCategoryFilter = (props) => (
    <Filter {...props}>
        <ReferenceInput
            label="Select a Product Type to get started."
            source="type"
            reference="ProductTypes"
            perPage={10000}
            validate={[required]}
            allowEmpty
            alwaysOn
            style={viewStyles.filterLeft}>
            <VirtualizedSelectInput
                addField={true}
                optionText="name"
                optionValue="name"
                options={{
                    clearable: false,
                    labelKey: 'name',
                    className: 'half-width',
                    placeholder: 'Select a Product Type to get started*'
                }}
            />
        </ReferenceInput>
    </Filter>
);

// Set title for List Product Categories
const ListTitle = <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                                className="avatar-title"/>Product Categories</span>;

const ListDescription = () => {
    return (
        <span>
            <img src="img-diagram-Product-Category-List.png" alt="Diagram of Products - Product Categories"
                 style={viewStyles.diagramRight}/>
            <p><strong>Product Categories</strong> group related Products on the POS, so employees can navigate
                to the Product they need. A Product Category will have a list of Products related in a specific way.</p>
            <p>
                For example, A Product Category named <em>Food</em> will have all products that are food related.
                Another Product Category named <em>Toys</em> will have all products that are toy related.</p>
            <p>Product Categories may also have sub categories. For example, Product Category <em>Food</em> might have
                subcategories <em>Fruit</em>, <em>Vegetables</em>, <em>Meat</em>, etc. In those subcategories are
                Products related to the subcategory.</p>
            <p>Select a Product Type to view Categories by that Type. Sort Categories by columns. Click a Category's
                View icon to view its details, or Edit to modify. Click <strong>Create</strong> to add a new Category.</p>
            <hr style={viewStyles.break}/>
        </span>
    );
};

const ListDescriptionViewOnly = () => {
    return (
        <span>
            <img src="img-diagram-Product-Category-List.png" alt="Diagram of Products - Product Categories"
                 style={viewStyles.diagramRight}/>
            <p><strong>Product Categories</strong> group related Products on the POS, so employees can navigate
                to the Product they need. A Product Category will have a list of Products related in a specific way.</p>
            <p>
                For example, A Product Category named <em>Food</em> will have all products that are food related.
                Another Product Category named <em>Toys</em> will have all products that are toy related.</p>
            <p>Product Categories may also have sub categories. For example, Product Category <em>Food</em> might have
                subcategories <em>Fruit</em>, <em>Vegetables</em>, <em>Meat</em>, etc. In those subcategories are
                Products related to the subcategory.</p>
            <p>Select a Product Type to view Categories by that Type. Sort Categories by columns. Click a Category's
                View icon to view its details.</p>
            <hr style={viewStyles.break}/>
        </span>
    );
};

export const fuseProductCategoryList = (props) => (
    <List {...props} title={ListTitle} filters={<ProductCategoryFilter/>} desc={<ListDescription/>}
          perPage={10} sort={{field: 'name', order: 'ASC'}}>
        <Responsive
            small={
                <SimpleList
                    primaryText={record => record.name}
                    secondaryText={record => record.parent_name}
                />
            }
            medium={
                <Datagrid bodyOptions={{stripedRows: false, showRowHover: true}}>
                    <TextField source="name" label="Category Name"/>
                    <TextField source="id" label="Category ID"/>
                    <TextField source="parent_name" label="Parent"/>
                    <TextField source="color_code" label="Color Code"/>
                    <BooleanField source="active" label="Active" elStyle={styles.fieldSmall} sortable={false}/>
                    <ViewButtonWithType/>
                    <EditButtonWithType/>
                </Datagrid>
            }
        />
    </List>
);

export const fuseProductCategoryListViewOnly = (props) => (
    <List {...props} title={ListTitle} filters={<ProductCategoryFilter/>} desc={<ListDescriptionViewOnly/>}
          perPage={10} sort={{field: 'name', order: 'ASC'}}>
        <Responsive
            small={
                <SimpleList
                    primaryText={record => record.name}
                    secondaryText={record => record.parent_name}
                />
            }
            medium={
                <Datagrid bodyOptions={{stripedRows: false, showRowHover: true}}>
                    <TextField source="name" label="Category Name"/>
                    <TextField source="id" label="Category ID"/>
                    <TextField source="parent_name" label="Parent"/>
                    <TextField source="color_code" label="Color Code"/>
                    <BooleanField source="active" label="Active" elStyle={styles.fieldSmall} sortable={false}/>
                    <ViewButtonWithType/>
                </Datagrid>
            }
        />
    </List>
);

/*
 * View: Show
 */

// Set title for Show Product Category
const ShowTitle = ({record}) => {
    return <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                         className="avatar-title"/>{record ? `${record.name}` : 'Product Category'}</span>;
};

const ShowDescription = () => {
    return (
        <span>
            <p>Review Category details below. To edit this Category, click <strong>Edit</strong> above.</p>
        </span>
    );
};

// Replace Actions for Show Product
const ShowActions = ({basePath, data, canEdit}) => (
    <CardActions style={viewStyles.actions}>
        {canEdit &&
        <EditButton basePath={basePath} record={data}/>
        }
        <ListButton basePath={getBasePathUpOne(basePath)}/>
        <RefreshButton/>
    </CardActions>
);

/*
 * Show Product Category Table
 */
export const fuseProductCategoryShow = ({canEdit, ...props}) => (
    <ShowWithType {...props} title={<ShowTitle/>} desc={<ShowDescription/>}
                  actions={<ShowActions canEdit={canEdit}/>}>
        <SimpleShowLayout {...props}>
            <TextField source="name" label="Category Name"/>
            <TextField source="id" label="Category ID"/>
            <TextField source="color_code" label="Color Code"/>
            <TextField source="type" label="Product Type"/>
            <TextField source="parent_name" label="Parent Category"
                       style={{...viewStyles.rowShowField, ...styles.rowShowField}}/>
            <TextField source="parent_id" label="Parent Category ID"
                       style={{...viewStyles.rowShowField, ...styles.rowShowField}}/>
            <hr style={viewStyles.break}/>
            <BooleanField source="active" label="Category is Active"
                          style={{...viewStyles.rowShowField, ...styles.rowShowField}} elStyle={styles.fieldSmall}/>
        </SimpleShowLayout>
    </ShowWithType>
);

/*
 * View: Create form
 */

// Set title for Create Product Category
const CreateTitle = <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                                  className="avatar-title"/>Create a Product Category</span>;

const CreateDescription = () => {
    return (
        <span>
            <p><strong>Product Categories</strong> group related Products on the POS, so employees can navigate
                to the Product they need. A Product Category will have a list of Products related in a specific way.</p>
            <p>
                For example, A Product Category named <em>Food</em> will have all products that are food related.
                Another Product Category named <em>Toys</em> will have all products that are toy related.</p>
            <p>Product Categories may also have sub categories. For example, Product Category <em>Food</em> might have
                subcategories <em>Fruit</em>, <em>Vegetables</em>, <em>Meat</em>, etc. In those subcategories are
                Products related to the subcategory.</p>
            <p>Enter the required fields below.</p>
        </span>
    );
};

// Set defaults for Create form
const defaultCreateValues = {active: false, parent_id: null, parent_name: null};

/*
 * Create Product Category form
 */
export const fuseProductCategoryCreate = (props) => (
    <Create {...props} title={CreateTitle} desc={<CreateDescription/>}>
        <SimpleForm defaultValue={defaultCreateValues} submitOnEnter={false} redirect="list">
            <TextInput source="name" label="Product Category Name" validate={[required]}/>
            <span style={viewStyles.fieldDescription}>
                The <strong>Name</strong> of the the Category.
            </span>

            <ReferenceInput
                label="Select a Product Type*"
                source="type"
                reference="ProductTypes"
                perPage={10000}
                allowEmpty>
                <VirtualizedSelectInput
                    addField={true}
                    optionText="name"
                    optionValue="name"
                    required={true}
                    options={{
                        clearable: false,
                        labelKey: 'name',
                        placeholder: 'Select a Product Type*',
                        required: true
                    }}
                />
            </ReferenceInput>
            <span style={viewStyles.fieldDescription}>
                Select a <strong>Product Type</strong>.
                <br/>
                (If Type is not available, please exit and go
                    to <a href="#/ProductTypes/create" style={viewStyles.fieldLink}>Add Product Type</a>).
                <TooltipIcon dataFor="ttType"/>
                <ReactTooltip id="ttType" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    A <strong>Product Type</strong> is used for configuring the POS System to display products to be
                    sold dependent on your Location's DBA. In most cases, a business will only have one Product Type.
                </ReactTooltip>
            </span>

            <DependentInput dependsOn="type">
                <ProductCategoryByTypeDependentSelectInput
                    {...props}
                    source="parent_id"
                    dependentProp="dependsOnValue"
                    optionValue="id"
                    optionText="name"
                    label="Select a parent Category"
                    allowEmpty/>
                <span style={viewStyles.fieldDescription}>
                    If this Category has a <strong>parent Category</strong>, select one of this Product Type.
                    <br/>
                    (If this Category requires a parent Category that is not available, please exit and go
                    to <a href="#/ProductCategories/create" style={viewStyles.fieldLink}>Add Product Category</a>).
                </span>
            </DependentInput>

            <ColorPickerTextInput source="color_code" label="Select a Color"/>
            <span style={viewStyles.fieldDescription}>
                Select a Category <strong>Color</strong>.
                <TooltipIcon dataFor="ttColor"/>
                <ReactTooltip id="ttColor" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    A <strong>Color</strong> acts to easily identify Categories on the POS. Selecting a Color will be
                    stored as a hex value (#000000). A Color is optional.
                </ReactTooltip>
            </span>

            <SelectInput source="active" label="Display Category on POS"
                         validate={[required]}
                         choices={[
                             {id: true, name: 'Yes'},
                             {id: false, name: 'No'}
                         ]}/>
            <span style={viewStyles.fieldDescription}>
                Select Yes or No.
                <TooltipIcon dataFor="ttActive"/>
                <ReactTooltip id="ttActive" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    Selecting <em>Yes</em> will display the Category and all Products associated with it on the POS.
                    <br/>
                    Selecting <em>No</em> will remove the Category from the POS so it can not be selected. Products in
                    this Category will also not be displayed or sold.
                </ReactTooltip>
            </span>

            <span style={viewStyles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to Product Category List.
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
        </SimpleForm>
    </Create>
);

/*
 * View: Edit form
 */

// Set title for Edit Product Category
const EditTitle = ({record}) => {
    return <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                         className="avatar-title"/>Edit {record ? `${record.name}` : 'Product Category'}</span>;
};

const EditDescription = () => {
    return (
        <span>
            <p>Modify Category fields below. Click <strong>Save</strong> to store this Category.</p>
        </span>
    );
};

// Replace Actions for Edit Product
const EditActions = ({basePath, data}) => (
    <CardActions style={viewStyles.actions}>
        <ShowButton basePath={basePath} record={data}/>
        <ListButton basePath={getBasePathUpOne(basePath)}/>
        <RefreshButton/>
    </CardActions>
);

/*
 * Edit Product Category form
 */
export const fuseProductCategoryEdit = (props) => (
    <EditWithType {...props} title={<EditTitle/>} desc={<EditDescription/>} actions={<EditActions/>}>
        <SimpleForm defaultValue={defaultCreateValues} submitOnEnter={false} redirect="list">
            <TextInput source="name" label="Product Category Name" validate={[required]}/>
            <TextField source="id" label="ID"
                       style={{...viewStyles.rowShowField, ...styles.rowShowField}}/>

            <ReferenceInput
                label="Select a new Product Type*"
                source="type"
                reference="ProductTypes"
                perPage={10000}
                allowEmpty>
                <VirtualizedSelectInput
                    addField={true}
                    optionText="name"
                    optionValue="name"
                    required={true}
                    options={{
                        clearable: false,
                        labelKey: 'name',
                        placeholder: 'Select a new Product Type*',
                        required: true
                    }}
                />
            </ReferenceInput>

            <span style={viewStyles.fieldDescription}>Note: Changing <strong>Product Type</strong> does not reassign this
                Product Category to the new Type. It will create a new Product Category of that Type, or update an
                existing one.
                <br/>
                (If a new Type is needed and not available, please exit and go
                    to <a href="#/ProductTypes/create" style={viewStyles.fieldLink}>Add Product Type</a>).
                <TooltipIcon dataFor="ttType"/>
                <ReactTooltip id="ttType" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    A <strong>Product Type</strong> is used for configuring the POS System to display products to be
                    sold dependent on your Location's DBA. In most cases, a business will only have one Product Type.
                </ReactTooltip>
            </span>

            <span style={viewStyles.fieldHeader}>Parent Category</span>
            <DependentInput dependsOn="type">
                <TextField source="current_parent_name" label="Current Parent"
                           style={{...viewStyles.rowShowField, ...styles.rowShowField}}/>
                <TextField source="current_parent_id" label="ID"
                           style={{...viewStyles.rowShowField, ...styles.rowShowField}}/>
                <ProductCategoryByTypeDependentSelectInput
                    {...props}
                    source="parent_id"
                    dependentProp="dependsOnValue"
                    optionValue="id"
                    optionText="name"
                    label="Select a parent Category"
                    allowEmpty/>
                <span style={viewStyles.fieldDescription}>
                    If this Category has a <strong>parent Category</strong> which needs to be edited, select one of its
                    Product Type.
                    <br/>
                    (If this Category requires a parent Category that is not available, please exit and go
                    to <a href="#/ProductCategories/create" style={viewStyles.fieldLink}>Add Product Category</a>).
                </span>
            </DependentInput>

            <ColorPickerTextInput source="color_code" label="Select a Color"/>
            <span style={viewStyles.fieldDescription}>
                Select a Category <strong>Color</strong>.
                <TooltipIcon dataFor="ttColor"/>
                <ReactTooltip id="ttColor" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    A <strong>Color</strong> acts to easily identify Categories on the POS. Selecting a Color will be
                    stored as a hex value (#000000). A Color is optional.
                </ReactTooltip>
            </span>

            <SelectInput source="active" label="Display Category on POS"
                         validate={[required]}
                         choices={[
                             {id: true, name: 'Yes'},
                             {id: false, name: 'No'}
                         ]}/>
            <span style={viewStyles.fieldDescription}>
                Select Yes or No.
                <TooltipIcon dataFor="ttActive"/>
                <ReactTooltip id="ttActive" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    Selecting <em>Yes</em> will display the Category and all Products associated with it on the POS.
                    <br/>
                    Selecting <em>No</em> will remove the Category from the POS so it can not be selected. Products in
                    this Category will also not be displayed or sold.
                </ReactTooltip>
            </span>

            <span style={viewStyles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to Product Category List.
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
        </SimpleForm>
    </EditWithType>
);



// WEBPACK FOOTER //
// ./src/Fuse/Product/category.js